<template>
  <div class="magazine-reader" @click.self="onBackdropClick">
    <div class="magazine-reader__container">
      <!-- 顶部工具栏 -->
      <div class="magazine-reader__header">
        <template v-if="!hideTopOperations">
          <div class="magazine-reader__share" @click="onShare"></div>
          <span class="magazine-reader__title">{{ magazineName || '电子杂志' }}</span>
          <div class="magazine-reader__close" @click="onClose">
            <svg viewBox="0 0 24 24" width="32" height="32">
              <path d="M6 9l6 6 6-6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
        </template>
        <template v-else>
          <span></span>
          <span class="magazine-reader__title">{{ magazineName || '电子杂志' }}</span>
          <span></span>
        </template>
      </div>

      <!-- 页码工具栏 -->
      <div
        v-show="!loading && totalPages > 0"
        class="magazine-reader__page-bar"
        @click="showPageList = true"
      >
        <span class="magazine-reader__page-info">第 {{ currentPage + 1 }} / {{ totalPages }} 页</span>
        <svg viewBox="0 0 24 24" width="20" height="20">
          <path d="M6 9l6 6 6-6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>

      <!-- 翻书舞台 -->
      <div class="magazine-reader__stage" ref="bookStage">
        <div v-show="loading" class="magazine-reader__loading">
          <div class="magazine-reader__spinner"></div>
          <span>正在加载杂志...</span>
        </div>

        <div ref="flipbook" class="magazine-reader__flipbook"></div>

        <!-- 左右翻页箭头 -->
        <div
          v-show="!loading"
          class="magazine-reader__nav magazine-reader__nav--left"
          @click="flipPrev"
        >
          <div class="magazine-reader__nav-icon">
            <svg viewBox="0 0 24 24" width="36" height="36">
              <path d="M15 6l-6 6 6 6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
        </div>
        <div
          v-show="!loading"
          class="magazine-reader__nav magazine-reader__nav--right"
          @click="flipNext"
        >
          <div class="magazine-reader__nav-icon">
            <svg viewBox="0 0 24 24" width="36" height="36">
              <path d="M9 6l6 6-6 6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
        </div>
      </div>

      <!-- 底部工具栏 -->
      <div class="magazine-reader__footer">
        <div class="magazine-reader__catalog-btn" @click="showCatalog = true">
          <svg viewBox="0 0 24 24" width="22" height="22">
            <rect x="3" y="3" width="7" height="7" rx="1" fill="none" stroke="currentColor" stroke-width="2"/>
            <rect x="14" y="3" width="7" height="7" rx="1" fill="none" stroke="currentColor" stroke-width="2"/>
            <rect x="3" y="14" width="7" height="7" rx="1" fill="none" stroke="currentColor" stroke-width="2"/>
            <rect x="14" y="14" width="7" height="7" rx="1" fill="none" stroke="currentColor" stroke-width="2"/>
          </svg>
          <span>全部目录</span>
        </div>
        <div v-if="!hideBuy" class="magazine-reader__buy-btn" @click="onBuy">
          <svg viewBox="0 0 24 24" width="20" height="20">
            <path d="M6 6h15l-1.5 9h-12z" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <circle cx="9" cy="20" r="1.5" fill="currentColor"/>
            <circle cx="18" cy="20" r="1.5" fill="currentColor"/>
            <path d="M6 6L5 3H2" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <span>购买本期杂志</span>
        </div>
      </div>
    </div>

    <!-- 目录弹层 -->
    <transition name="magazine-reader__slide">
      <div v-if="showCatalog" class="magazine-reader__catalog" @click.self="showCatalog = false">
        <div class="magazine-reader__catalog-panel">
          <div class="magazine-reader__catalog-header">
            <div class="magazine-reader__catalog-title">
              <svg viewBox="0 0 24 24" width="32" height="32">
                <line x1="4" y1="6" x2="20" y2="6" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                <line x1="4" y1="12" x2="20" y2="12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                <line x1="4" y1="18" x2="20" y2="18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              </svg>
              <span>全部目录</span>
            </div>
            <div class="magazine-reader__catalog-close" @click="showCatalog = false">
              <svg viewBox="0 0 24 24" width="24" height="24">
                <path d="M6 9l6 6 6-6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
          </div>
          <div class="magazine-reader__catalog-list">
            <div
              v-for="nav in catalogList"
              :key="nav.id"
              class="magazine-reader__catalog-item"
              @click="jumpToCatalogPage(nav)"
            >
              <span class="magazine-reader__catalog-page">{{ formatPage(nav.turnPage) }}</span>
              <span class="magazine-reader__catalog-type">{{nav.navTypeName }}</span>
              <span class="magazine-reader__catalog-name">{{ nav.navName }}</span>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <!-- 页面列表弹层 -->
    <transition name="magazine-reader__slide">
      <div v-if="showPageList" class="magazine-reader__page-list" @click.self="showPageList = false">
        <div class="magazine-reader__page-list-panel">
          <div class="magazine-reader__page-list-header">
            <div class="magazine-reader__page-list-title">
              <svg viewBox="0 0 24 24" width="32" height="32">
                <rect x="3" y="3" width="7" height="7" rx="1" fill="none" stroke="currentColor" stroke-width="2"/>
                <rect x="14" y="3" width="7" height="7" rx="1" fill="none" stroke="currentColor" stroke-width="2"/>
                <rect x="3" y="14" width="7" height="7" rx="1" fill="none" stroke="currentColor" stroke-width="2"/>
                <rect x="14" y="14" width="7" height="7" rx="1" fill="none" stroke="currentColor" stroke-width="2"/>
              </svg>
              <span>选择页面</span>
            </div>
            <div class="magazine-reader__page-list-close" @click="showPageList = false">
              <svg viewBox="0 0 24 24" width="24" height="24">
                <path d="M6 9l6 6 6-6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
          </div>
          <div class="magazine-reader__page-list-body">
            <div
              v-for="(url, index) in pageUrls"
              :key="index"
              class="magazine-reader__page-list-item"
              :class="{ 'magazine-reader__page-list-item--active': currentPage === index }"
              @click="jumpToPageAndClose(index)"
            >
              <div class="magazine-reader__page-list-thumb">
                <img v-if="url" :src="url" :alt="`第${index + 1}页`" loading="lazy" />
                <div v-else class="magazine-reader__page-list-placeholder">加载中</div>
              </div>
              <span class="magazine-reader__page-list-number">{{ index + 1 }}</span>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <!-- 购买弹窗 -->
    <div v-if="showBuyDialog" class="mag-buy-dialog-overlay" @click.self="closeBuyDialog">
      <!-- 选择页面 -->
      <transition name="mag-dialog-fade">
        <div v-if="!showPaySuccess" class="mag-buy-dialog mag-buy-dialog-select">
          <div class="mag-dialog-header">
            <h3>{{ magazineName || '电子杂志' }}</h3>
          </div>

          <div class="mag-dialog-content">
            <!-- 选择类型 -->
            <div class="mag-select-section">
              <label class="mag-label">请选择您购买的类型：</label>
              <div class="mag-radio-group">
                <label class="mag-radio-item">
                  <input type="radio" value="single" v-model="buyType" @change="handleProductTypeChange">
                  <span class="mag-radio-text">本期</span>
                </label>
                <label class="mag-radio-item">
                  <input type="radio" value="subscription" v-model="buyType" @change="handleProductTypeChange">
                  <span class="mag-radio-text">年刊</span>
                </label>
              </div>
            </div>

            <!-- 杂志类型 -->
            <div class="mag-select-section">
              <label class="mag-label">杂志类型：</label>
              <div class="mag-radio-group">
                <label class="mag-radio-item">
                  <input type="radio" value="print" v-model="mediaType" @change="calculateTotalPrice">
                  <span class="mag-radio-text">纸质版本 (包含电子版本)</span>
                </label>
              </div>
            </div>

            <!-- 单本金额 -->
            <div class="mag-info-row">
              <span class="mag-info-label">单本金额：</span>
              <span class="mag-info-value">¥{{ currentPrice.toFixed(2) }}</span>
            </div>

            <!-- 购买数量 -->
            <div class="mag-quantity-section">
              <label class="mag-label">购买数量：</label>
              <div class="mag-quantity-control">
                <button class="mag-qty-btn" @click.stop="decreaseCount" :disabled="buyCount <= 1">-</button>
                <span class="mag-qty-value">{{ buyCount }}</span>
                <button class="mag-qty-btn" @click.stop="increaseCount" :disabled="buyCount >= 99">+</button>
              </div>
            </div>

            <!-- 支付金额 -->
            <div class="mag-total-section">
              <label class="mag-total-label">支付金额：</label>
              <span class="mag-total-value">¥{{ totalPrice.toFixed(2) }}</span>
            </div>

            <!-- ===== 收货人信息 ===== -->
            <div class="mag-address-section">
              <!-- 收货人姓名 -->
              <div class="mag-input-row">
                <label class="mag-label">收货人姓名：</label>
                <input
                  type="text"
                  v-model="receiverName"
                  placeholder="请输入收货人姓名"
                  maxlength="20"
                  class="mag-input-text"
                />
              </div>

              <!-- 手机号码 -->
              <div class="mag-input-row">
                <label class="mag-label">手机号码：</label>
                <input
                  type="tel"
                  v-model="receiverPhone"
                  placeholder="请输入手机号码"
                  maxlength="11"
                  class="mag-input-text"
                  @input="validatePhone"
                />
              </div>

              <!-- 所在地区（自定义下拉） -->
              <div class="mag-district-section">
                <label class="mag-label">所在地区：</label>
                <div class="mag-district-row">
                  <!-- 省份 -->
                  <div class="mag-custom-select" :class="{ 'mag-custom-select--active': !!receiverProvince }">
                    <div class="mag-custom-select__trigger" @click.stop="openPicker('province')">
                      <span :class="{ 'mag-custom-select__placeholder': !receiverProvince }">
                        {{ receiverProvince ? provinceName : '选择省份' }}
                      </span>
                      <svg class="mag-custom-select__arrow" viewBox="0 0 24 24" width="14" height="14">
                        <path d="M6 9l6 6 6-6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                    </div>
                  </div>

                  <!-- 城市 -->
                  <div class="mag-custom-select" :class="{ 'mag-custom-select--active': !!receiverCity, 'mag-custom-select--disabled': !receiverProvince }">
                    <div class="mag-custom-select__trigger" @click.stop="receiverProvince && openPicker('city')">
                      <span :class="{ 'mag-custom-select__placeholder': !receiverCity }">
                        {{ receiverCity ? cityName : '选择城市' }}
                      </span>
                      <svg class="mag-custom-select__arrow" viewBox="0 0 24 24" width="14" height="14">
                        <path d="M6 9l6 6 6-6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                    </div>
                  </div>

                  <!-- 区县 -->
                  <div class="mag-custom-select" :class="{ 'mag-custom-select--active': !!receiverDistrict, 'mag-custom-select--disabled': !receiverCity }">
                    <div class="mag-custom-select__trigger" @click.stop="receiverCity && openPicker('district')">
                      <span :class="{ 'mag-custom-select__placeholder': !receiverDistrict }">
                        {{ receiverDistrict ? districtName : '选择区县' }}
                      </span>
                      <svg class="mag-custom-select__arrow" viewBox="0 0 24 24" width="14" height="14">
                        <path d="M6 9l6 6 6-6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                    </div>
                  </div>
                </div>

                <!-- 限制配送地区提示 -->
                <div class="mag-warning-text" v-if="isRestrictedRegion">
                  新疆、西藏地区暂不支持配送
                </div>
              </div>

              <!-- 详细地址 -->
              <div class="mag-input-row mag-input-row-textarea">
                <label class="mag-label">详细地址：</label>
                <textarea
                  v-model="receiverAddress"
                  placeholder="请输入详细地址"
                  maxlength="100"
                  rows="3"
                  class="mag-input-textarea"
                ></textarea>
              </div>
            </div>

            <!-- 立即支付按钮 -->
            <button
              class="mag-pay-btn"
              :disabled="loading || isRestrictedRegion"
              @click.stop="onPayment"
            >
              {{ loading ? '支付中...' : '立刻支付' }}
            </button>
          </div>
        </div>
      </transition>

      <!-- 支付成功页面 -->
      <transition name="mag-dialog-fade">
        <div v-if="showPaySuccess" class="mag-buy-dialog mag-buy-dialog-success">
          <div class="mag-dialog-content mag-dialog-content--center">
            <div class="mag-success-icon">
              <svg viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="11" stroke="#52c41a" stroke-width="1.5"/>
                <path d="M7 12.5L10.5 16L17 9" stroke="#52c41a" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>

            <h3 class="mag-success-title">支付成功</h3>

            <p class="mag-success-tip">
              您已经支付成功，可以去<span class="mag-success-link" @click="goToOrders">我的订单</span>查看电子杂志订单
            </p>
          </div>
        </div>
      </transition>
    </div>

    <!-- ===== 省市区 ActionSheet 选择器 ===== -->
    <transition name="mag-actionsheet-fade">
      <div
        v-if="pickerVisible"
        class="mag-actionsheet-mask"
        @click.self="closePicker"
      >
        <transition name="mag-actionsheet-slide">
          <div v-if="pickerVisible" class="mag-actionsheet">
            <!-- 头部 -->
            <div class="mag-actionsheet__header">
              <span class="mag-actionsheet__cancel" @click="closePicker">取消</span>
              <span class="mag-actionsheet__title">{{ pickerTitle }}</span>
              <span class="mag-actionsheet__placeholder"></span>
            </div>

            <!-- 选项列表 -->
            <div class="mag-actionsheet__body">
              <div
                v-for="item in pickerList"
                :key="item.value"
                class="mag-actionsheet__item"
                :class="{ 'is-active': pickerSelected === item.value }"
                @click="confirmPicker(item)"
              >
                <span class="mag-actionsheet__item-text">{{ item.label }}</span>
                <svg
                  v-if="pickerSelected === item.value"
                  class="mag-actionsheet__check"
                  viewBox="0 0 24 24"
                  width="20"
                  height="20"
                >
                  <path d="M5 12l5 5L20 7" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
            </div>
          </div>
        </transition>
      </div>
    </transition>
  </div>
</template>

<script>
import { PageFlip } from 'page-flip';
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf';
import { getMagazineView } from '@/api/magazine';
import { createOrder, wxUnifiedOrder, getPayStatus } from '@/api/order';
// 引入完整省市区数据
import { regionData } from 'element-china-area-data';

pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.worker.min.js';

/* global WeixinJSBridge */
export default {
  name: 'MagazineReader',
  props: {
    emagazineCode: {
      type: String,
      default: ''
    },
    codeToken: {
      type: String,
      default: ''
    },
    hideBuy: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      loading: true,
      pageFlip: null,
      pageUrls: [],
      totalPages: 0,
      currentPage: 0,
      isMounted: true,
      mouseDownHandler: null,
      mouseUpHandler: null,
      resizeTimer: null,
      pdfDoc: null,
      showCatalog: false,
      showPageList: false,
      showBuyDialog: false,
      showPaySuccess: false,
      buyType: 'single',
      mediaType: 'print',
      buyCount: 1,
      prices: {
        singleDigitalPrice: 0,
        singlePrintPrice: 0,
        subscriptionDigitalPrice: 0,
        subscriptionPrintPrice: 0
      },
      magazineName: '',
      catalogList: [],
      navTypeMap: {
        1: '卷首',
        2: '数据',
        3: '聚焦',
        4: '快消',
        5: '话题',
        6: '科技'
      },

      // ===== 收货人信息 =====
      receiverName: '',
      receiverPhone: '',
      receiverAddress: '',
      receiverProvince: '',
      receiverCity: '',
      receiverDistrict: '',

      // ===== 省市区数据 =====
      regionData: regionData,
      provinceList: [],
      cityList: [],
      districtList: [],

      restrictedRegions: ['54', '65'], // 西藏、新疆

      // ===== ActionSheet 选择器状态 =====
      pickerVisible: false,     // 是否显示
      pickerType: '',           // 'province' | 'city' | 'district'
      pickerTitle: '',          // 标题
      pickerList: [],           // 当前展示的选项列表
      pickerSelected: ''        // 当前选中的值
    };
  },
  computed: {
    currentPrice() {
      if (this.buyType === 'single') {
        return this.mediaType === 'digital' ? this.prices.singleDigitalPrice : this.prices.singlePrintPrice;
      } else {
        return this.mediaType === 'digital' ? this.prices.subscriptionDigitalPrice : this.prices.subscriptionPrintPrice;
      }
    },
    totalPrice() {
      return this.currentPrice * this.buyCount;
    },
    hideTopOperations() {
      return !!this.codeToken;
    },
    isRestrictedRegion() {
      return this.restrictedRegions.includes(this.receiverProvince);
    },
    provinceName() {
      const p = this.provinceList.find(item => item.value === this.receiverProvince);
      return p ? p.label : '';
    },
    cityName() {
      const c = this.cityList.find(item => item.value === this.receiverCity);
      return c ? c.label : '';
    },
    districtName() {
      const d = this.districtList.find(item => item.value === this.receiverDistrict);
      return d ? d.label : '';
    }
  },
  watch: {
    emagazineCode(val) {
      if (val) {
        this.resetAndLoad();
      }
    }
  },
  async mounted() {
    window.addEventListener('resize', this.handleResize);

    this.provinceList = this.regionData.map(p => ({
      value: p.value,
      label: p.label
    }));

    if (this.emagazineCode) {
      await this.resetAndLoad();
      const buyCode = localStorage.getItem('magazine_buy_code');
      const token = localStorage.getItem('token');
      if (buyCode && buyCode === this.emagazineCode && token) {
        localStorage.removeItem('magazine_buy_code');
        this.openBuyDialog();
      }
    }
  },
  beforeDestroy() {
    this.isMounted = false;
    window.removeEventListener('resize', this.handleResize);
    if (this.resizeTimer) clearTimeout(this.resizeTimer);
    this.cleanupHandlers();
    if (this.pageFlip) {
      try { this.pageFlip.destroy(); } catch (e) { /* 忽略 */ }
      this.pageFlip = null;
    }
  },
  methods: {
    // ==================== PDF 加载 ====================
    resetAndLoad() {
      this.cleanupHandlers();
      if (this.pageFlip) {
        try { this.pageFlip.destroy(); } catch (e) { /* 忽略 */ }
        this.pageFlip = null;
      }
      this.pageUrls = [];
      this.totalPages = 0;
      this.currentPage = 0;
      this.pdfDoc = null;
      this.loading = true;
      this.$nextTick(() => {
        this.loadPdf();
      });
    },
    cleanupHandlers() {
      if (this.mouseDownHandler && this.$refs.flipbook) {
        this.$refs.flipbook.removeEventListener('mousedown', this.mouseDownHandler);
      }
      if (this.mouseUpHandler) {
        window.removeEventListener('mouseup', this.mouseUpHandler);
      }
      this.mouseDownHandler = null;
      this.mouseUpHandler = null;
    },
    async loadPdf() {
      try {
        if (!this.emagazineCode) {
          console.error('缺少 emagazineCode');
          this.loading = false;
          return;
        }
        const params = { emagazineCode: this.emagazineCode };
        if (this.codeToken) {
          params.codeToken = this.codeToken;
        }
        const res = await getMagazineView(params);
        const data = (res.data || {});
        this.magazineName = data.name || '电子杂志';
        this.prices = {
          singleDigitalPrice: data.singleDigitalPrice || 0,
          singlePrintPrice: data.singlePrintPrice || 0,
          subscriptionDigitalPrice: data.subscriptionDigitalPrice || 0,
          subscriptionPrintPrice: data.subscriptionPrintPrice || 0
        };
        this.catalogList = (data.navList || []).sort((a, b) => a.sortOrder - b.sortOrder);
        const fullPdfPath = data.pdfPath;
        if (!fullPdfPath) {
          console.error('接口未返回 pdfPath');
          this.loading = false;
          return;
        }
        const loadingTask = pdfjsLib.getDocument(fullPdfPath);
        this.pdfDoc = await loadingTask.promise;
        const numPages = this.pdfDoc.numPages;

        const pageUrls = new Array(numPages).fill(null);
        pageUrls[0] = await this.renderPage(1);
        this.pageUrls = pageUrls;

        this.$nextTick(() => {
          this.initPageFlip();
        });

        this.loadNextChunk(2, numPages, 5);
      } catch (err) {
        console.error('PDF 加载失败', err);
        this.loading = false;
      }
    },

    async loadNextChunk(startPage, numPages, chunkSize) {
      if (startPage > numPages) return;
      const endPage = Math.min(startPage + chunkSize - 1, numPages);
      const pageUrls = [...this.pageUrls];

      for (let i = startPage; i <= endPage; i++) {
        try {
          pageUrls[i - 1] = await this.renderPage(i);
        } catch (e) {
          console.error(`渲染第 ${i} 页失败`, e);
        }
      }
      this.pageUrls = pageUrls;
      this.patchPageImages(startPage, endPage);
      this.loadNextChunk(endPage + 1, numPages, chunkSize);
    },

    patchPageImages(startPage, endPage) {
      const container = this.$refs.flipbook;
      if (!container) return;
      const pages = container.querySelectorAll('.mag-flip-page');
      for (let i = startPage; i <= endPage; i++) {
        const pageEl = pages[i - 1];
        if (!pageEl) continue;
        const url = this.pageUrls[i - 1];
        if (!url) continue;
        let img = pageEl.querySelector('img');
        if (!img) {
          img = document.createElement('img');
          img.draggable = false;
          pageEl.innerHTML = '';
          pageEl.appendChild(img);
        }
        if (img.src !== url) {
          img.src = url;
        }
      }
    },

    async renderPage(pageNum) {
      const page = await this.pdfDoc.getPage(pageNum);
      const scale = 1.5;
      const viewport = page.getViewport({ scale });
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      await page.render({ canvasContext: ctx, viewport }).promise;
      const dataUrl = canvas.toDataURL('image/jpeg', 0.85);

      try {
        const img = new Image();
        img.src = dataUrl;
        if (img.decode) await img.decode();
      } catch (e) { /* 忽略 */ }

      return dataUrl;
    },

    createPageElements() {
      const pages = [];
      if (this.pageUrls.length === 0) return pages;

      const coverPage = document.createElement('div');
      coverPage.className = 'mag-flip-page mag-cover-page';
      coverPage.setAttribute('data-density', 'hard');
      coverPage.setAttribute('data-page-index', '0');
      if (this.pageUrls[0]) {
        const coverImg = document.createElement('img');
        coverImg.src = this.pageUrls[0];
        coverImg.draggable = false;
        coverPage.appendChild(coverImg);
      } else {
        coverPage.appendChild(this.createPagePlaceholder());
      }
      pages.push(coverPage);

      this.pageUrls.slice(1).forEach((url, idx) => {
        const page = document.createElement('div');
        page.className = 'mag-flip-page';
        page.setAttribute('data-page-index', String(idx + 1));
        if (url) {
          const img = document.createElement('img');
          img.src = url;
          img.draggable = false;
          page.appendChild(img);
        } else {
          page.appendChild(this.createPagePlaceholder());
        }
        pages.push(page);
      });

      const backCover = document.createElement('div');
      backCover.className = 'mag-flip-page mag-flip-page-back mag-back-cover-page';
      backCover.setAttribute('data-density', 'hard');
      backCover.setAttribute('data-page-index', String(this.pageUrls.length));
      pages.push(backCover);

      return pages;
    },

    createPagePlaceholder() {
      const div = document.createElement('div');
      div.className = 'mag-page-placeholder';
      div.innerHTML = '<span>加载中...</span>';
      return div;
    },

    initPageFlip() {
      const container = this.$refs.flipbook;
      if (!container) return;

      this.cleanupHandlers();
      if (this.pageFlip) {
        try { this.pageFlip.destroy(); } catch (e) { /* 忽略 */ }
        this.pageFlip = null;
      }

      container.innerHTML = '';
      container.className = 'magazine-reader__flipbook';

      const pages = this.createPageElements();

      this.pageFlip = new PageFlip(container, {
        width: 615,
        height: 795,
        size: 'fixed',
        showCover: true,
        maxShadowOpacity: 0,
        flippingTime: 800,
        drawShadow: false,
        usePortrait: true,
        autoSize: true,
        showPageCorners: true,
        useMouseEvents: true,
        mobileScrollSupport: false,
        swipeDistance: 30,
        clickEventForward: true,
        disableFlipByClick: true
      });

      this.pageFlip.loadFromHTML(pages);
      this.totalPages = this.pageFlip.getPageCount();

      this.pageFlip.on('flip', (e) => {
        this.currentPage = e.data;
      });

      let mouseDownOnBook = false;
      let mouseDownX = 0;
      let mouseDownY = 0;

      this.mouseDownHandler = (e) => {
        mouseDownOnBook = true;
        mouseDownX = e.clientX;
        mouseDownY = e.clientY;
      };

      this.mouseUpHandler = (e) => {
        if (!mouseDownOnBook) return;
        mouseDownOnBook = false;
        const dx = Math.abs(e.clientX - mouseDownX);
        const dy = Math.abs(e.clientY - mouseDownY);
        if (dx < 5 && dy < 5) {
          if (this.currentPage === 0) {
            this.flipNext();
          } else if (this.currentPage === this.totalPages - 1) {
            this.flipPrev();
          } else {
            const rect = container.getBoundingClientRect();
            const clickX = e.clientX - rect.left;
            if (clickX > rect.width / 2) {
              this.flipNext();
            } else {
              this.flipPrev();
            }
          }
        }
      };

      container.addEventListener('mousedown', this.mouseDownHandler);
      window.addEventListener('mouseup', this.mouseUpHandler);

      this.$nextTick(() => {
        requestAnimationFrame(() => {
          if (this.pageFlip) {
            try { this.pageFlip.update(); } catch (e) { /* 忽略 */ }
          }
          this.loading = false;
        });
      });
    },

    handleResize() {
      if (this.resizeTimer) clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => {
        if (this.loading || !this.pageFlip) return;
        this.pageFlip.update();
      }, 300);
    },

    withFlipClickEnabled(action) {
      if (!this.pageFlip) return;
      const original = this.pageFlip.setting.disableFlipByClick;
      this.pageFlip.setting.disableFlipByClick = false;
      try {
        action();
      } finally {
        this.pageFlip.setting.disableFlipByClick = original;
      }
    },

    flipNext() {
      if (!this.pageFlip || this.loading) return;
      const before = this.currentPage;
      try {
        this.withFlipClickEnabled(() => this.pageFlip.flipNext());
        setTimeout(() => {
          if (this.currentPage === before) {
            try { this.pageFlip.turnToNextPage(); } catch (e) { /* 忽略 */ }
          }
        }, 1000);
      } catch (e) {
        try { this.pageFlip.turnToNextPage(); } catch (e2) { /* 忽略 */ }
      }
    },

    flipPrev() {
      if (!this.pageFlip || this.loading) return;
      const before = this.currentPage;
      try {
        this.withFlipClickEnabled(() => this.pageFlip.flipPrev());
        setTimeout(() => {
          if (this.currentPage === before) {
            try { this.pageFlip.turnToPrevPage(); } catch (e) { /* 忽略 */ }
          }
        }, 1000);
      } catch (e) {
        try { this.pageFlip.turnToPrevPage(); } catch (e2) { /* 忽略 */ }
      }
    },

    jumpToPage(pageNum) {
      if (!this.pageFlip || this.loading) return;
      const before = this.currentPage;
      try {
        this.withFlipClickEnabled(() => this.pageFlip.flip(pageNum));
        setTimeout(() => {
          if (this.currentPage === before) {
            try { this.pageFlip.turnToPage(pageNum); } catch (e) { /* 忽略 */ }
          }
        }, 1000);
      } catch (e) {
        try { this.pageFlip.turnToPage(pageNum); } catch (e2) { /* 忽略 */ }
      }
    },

    jumpToCatalogPage(nav) {
      if (nav.turnPage == null) return;
      this.showCatalog = false;
      this.jumpToPage(Math.max(0, nav.turnPage - 1));
    },

    jumpToPageAndClose(pageNum) {
      this.jumpToPage(pageNum);
      this.showPageList = false;
    },

    onClose() {
      this.$emit('close');
    },

    onBackdropClick() {
      this.onClose();
    },

    onShare() {
      console.log('分享杂志:', this.magazineName);
    },

    onBuy() {
      if (this.prices.singleDigitalPrice === 0 && this.prices.singlePrintPrice === 0) {
        this.$message.error('未设置价格信息');
        return;
      }

      const token = localStorage.getItem('token');
      if (!token) {
        localStorage.setItem('magazine_buy_code', this.emagazineCode || '');
        const redirect = encodeURIComponent(this.$route.fullPath || window.location.pathname + window.location.search);
        this.$router.push(`/login?redirect=${redirect}`);
        return;
      }

      this.openBuyDialog();
    },

    openBuyDialog() {
      this.resetRemFontSize();
      this.loading = false;
      this.showPaySuccess = false;
      this.buyType = 'single';
      this.mediaType = 'print';
      this.buyCount = 1;
      // 清空收货信息
      this.receiverName = '';
      this.receiverPhone = '';
      this.receiverAddress = '';
      this.receiverProvince = '';
      this.receiverCity = '';
      this.receiverDistrict = '';
      this.cityList = [];
      this.districtList = [];
      this.$nextTick(() => {
        this.showBuyDialog = true;
      });
    },

    resetRemFontSize() {
      const size = (document.documentElement.clientWidth / 804) * 37.5;
      document.documentElement.style.fontSize = size + 'px';
    },

    handleProductTypeChange() {
      this.calculateTotalPrice();
    },

    calculateTotalPrice() {
      this.totalPrice = this.currentPrice * this.buyCount;
    },

    increaseCount() {
      this.buyCount++;
    },
    decreaseCount() {
      if (this.buyCount > 1) {
        this.buyCount--;
      }
    },

    // ==================== ActionSheet 选择器 ====================
    // 打开选择器
    openPicker(type) {
      this.pickerType = type;
      this.pickerSelected =
        type === 'province' ? this.receiverProvince :
        type === 'city' ? this.receiverCity :
        this.receiverDistrict;

      if (type === 'province') {
        this.pickerTitle = '选择省份';
        this.pickerList = this.provinceList;
      } else if (type === 'city') {
        this.pickerTitle = '选择城市';
        this.pickerList = this.cityList;
      } else if (type === 'district') {
        this.pickerTitle = '选择区县';
        this.pickerList = this.districtList;
      }

      this.pickerVisible = true;
    },

    // 关闭选择器
    closePicker() {
      this.pickerVisible = false;
    },

    // 确认选择
    confirmPicker(item) {
      if (this.pickerType === 'province') {
        this.receiverProvince = item.value;
        this.receiverCity = '';
        this.receiverDistrict = '';
        // 加载城市
        const p = this.regionData.find(i => i.value === item.value);
        this.cityList = p && p.children ? p.children.map(c => ({ value: c.value, label: c.label })) : [];
        this.districtList = [];
      } else if (this.pickerType === 'city') {
        this.receiverCity = item.value;
        this.receiverDistrict = '';
        // 加载区县
        const p = this.regionData.find(i => i.value === this.receiverProvince);
        if (p && p.children) {
          const c = p.children.find(i => i.value === item.value);
          this.districtList = c && c.children ? c.children.map(d => ({ value: d.value, label: d.label })) : [];
        } else {
          this.districtList = [];
        }
      } else if (this.pickerType === 'district') {
        this.receiverDistrict = item.value;
      }

      this.pickerVisible = false;
    },

    // ==================== 表单校验 ====================
    validatePhone() {
      this.receiverPhone = this.receiverPhone.replace(/[^0-9]/g, '');
    },

    canPay() {
      if (!this.receiverName.trim()) {
        alert('请输入收货人姓名');
        return false;
      }
      if (!this.receiverPhone || this.receiverPhone.length !== 11) {
        alert('请输入正确的手机号码');
        return false;
      }
      if (!this.receiverProvince) {
        alert('请选择所在地区');
        return false;
      }
      if (!this.receiverAddress.trim()) {
        alert('请输入详细地址');
        return false;
      }
      if (this.isRestrictedRegion) {
        alert('新疆、西藏地区暂不支持配送');
        return false;
      }
      return true;
    },

    // ==================== 支付 ====================
    async onPayment() {
      if (this.loading) return;
      if (!this.canPay()) return;

      const periodType = this.buyType === 'single' ? 1 : 2;
      const mediaType = this.mediaType === 'print' ? 2 : 1;

      this.loading = true;
      try {
        const createRes = await createOrder({
          emagazineCode: this.emagazineCode,
          periodType,
          mediaType,
          quantity: this.buyCount,
          receiverName: this.receiverName,
          receiverPhone: this.receiverPhone,
          receiverAddress: this.receiverAddress,
          receiverProvince: this.receiverProvince,
          receiverProvinceName: this.provinceName,
          receiverCity: this.receiverCity,
          receiverCityName: this.cityName,
          receiverDistrict: this.receiverDistrict,
          receiverDistrictName: this.districtName
        });
        const orderNo = createRes && (typeof createRes.data === 'string' ? createRes.data : createRes.data && createRes.data.orderNo);
        if (!orderNo) {
          throw new Error('创建订单失败，未返回订单编号');
        }

        await this.initWechatPay(orderNo);
      } catch (err) {
        console.error('支付失败:', err);
        this.loading = false;
        this.showPaySuccess = false;
        const msg = (err && err.message) || '支付失败，请稍后重试';
        this.$message && this.$message.error ? this.$message.error(msg) : alert(msg);
      }
    },

    async initWechatPay(orderNo) {
      const res = await wxUnifiedOrder({ orderNo, type: 'h5' });
      if (!res || res.code !== 200) {
        throw new Error(res.msg || '微信下单失败');
      }
      const wxData = res.data;
      if (!wxData.timeStamp || !wxData.package || !wxData.signType || !wxData.nonceStr || !wxData.paySign) {
        throw new Error('微信下单数据不完整');
      }

      const appId = wxData.appId || wxData.appid || process.env.VUE_APP_WECHAT_APPID || '';
      if (!appId) {
        throw new Error('缺少微信公众号 AppID');
      }

      return new Promise((resolve, reject) => {
        const payParams = {
          appId: appId,
          timeStamp: wxData.timeStamp,
          nonceStr: wxData.nonceStr,
          package: wxData.package,
          signType: wxData.signType || 'MD5',
          paySign: wxData.paySign
        };

        const callWechatPay = () => {
          WeixinJSBridge.invoke(
            'getBrandWCPayRequest',
            payParams,
            res => {
              if (res.err_msg === 'get_brand_wcpay_request:ok') {
                this.startPayStatusCheck(orderNo);
                resolve();
              } else if (res.err_msg === 'get_brand_wcpay_request:cancel') {
                reject(new Error('微信支付被取消'));
              } else {
                console.error('微信支付失败:', res.err_msg);
                reject(new Error('微信支付失败'));
              }
            }
          );
        };

        if (typeof WeixinJSBridge === 'undefined') {
          if (document.addEventListener) {
            document.addEventListener('WeixinJSBridgeReady', callWechatPay, false);
          } else if (document.attachEvent) {
            document.attachEvent('WeixinJSBridgeReady', callWechatPay);
            document.attachEvent('onWeixinJSBridgeReady', callWechatPay);
          }
        } else {
          callWechatPay();
        }
      });
    },

    startPayStatusCheck(orderNo) {
      this.loading = true;
      this.showPaySuccess = false;

      const checkInterval = setInterval(() => {
        getPayStatus({ orderNo })
          .then(res => {
            if (res && res.code === 200) {
              const payStatus = res.data && res.data.payStatus;
              if (payStatus === 1) {
                clearInterval(checkInterval);
                this.loading = false;
                this.showPaySuccess = true;
              }
            }
          })
          .catch(err => {
            console.error('查询支付状态失败:', err);
          });
      }, 1000);
    },

    goToOrders() {
      this.closeBuyDialog();
      this.$router.push('/my/orders');
    },

    closeBuyDialog() {
      this.showBuyDialog = false;
      this.showPaySuccess = false;
      this.pickerVisible = false;
    },

    formatIssue(num) {
      const n = parseInt(num, 10);
      return n < 10 ? `0${n}` : n;
    },

    formatPage(page) {
      if (page == null) return '--';
      const p = parseInt(page, 10);
      return p < 10 ? `0${p}` : p;
    }
  }
};
</script>

<style>
/* 阅读器遮罩 */
.magazine-reader {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  z-index: 1000;
  display: flex;
  flex-direction: column;
}

.magazine-reader__container {
  width: var(--page-width, 804px);
  height: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  background: #2a2a3a;
}

/* 顶部工具栏 */
.magazine-reader__header {
  height: 88px;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(12px);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
  flex-shrink: 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  z-index: 100;
}

.magazine-reader__title {
  color: #fff;
  font-size: 30px;
  font-family: 'Microsoft YaHei', sans-serif;
  letter-spacing: 2px;
}

.magazine-reader__close,
.magazine-reader__share {
  width: 72px;
  height: 72px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;
  border-radius: 50%;
  transition: all 0.25s;
}

.magazine-reader__close:hover,
.magazine-reader__share:hover {
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
}

/* 页码工具栏 */
.magazine-reader__page-bar {
  height: 64px;
  background: rgba(0, 0, 0, 0.35);
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: rgba(255, 255, 255, 0.85);
  font-size: 26px;
  font-family: 'Microsoft YaHei', sans-serif;
  cursor: pointer;
  flex-shrink: 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  transition: all 0.25s;
}

.magazine-reader__page-bar:hover {
  background: rgba(0, 0, 0, 0.5);
  color: #fff;
}

.magazine-reader__page-bar svg {
  transition: transform 0.25s;
}

.magazine-reader__page-bar:hover svg {
  transform: translateY(2px);
}

.magazine-reader__close svg {
  transform: rotate(-90deg);
}

/* 翻书舞台 */
.magazine-reader__stage {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  padding: 10px;
  position: relative;
  min-height: 0;
}

.magazine-reader__loading {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  color: rgba(255, 255, 255, 0.5);
  font-size: 24px;
  z-index: 50;
}

.magazine-reader__spinner {
  width: 48px;
  height: 48px;
  border: 4px solid rgba(255, 255, 255, 0.1);
  border-top-color: rgba(160, 0, 0, 0.8);
  border-radius: 50%;
  animation: mag-spin 0.8s linear infinite;
}

@keyframes mag-spin {
  to { transform: rotate(360deg); }
}

.magazine-reader__flipbook {
  width: 100%;
  height: 100%;
  align-self: stretch;
  overflow: hidden;
}

/* page-flip 页面元素 */
.mag-flip-page {
  background: #fff;
  overflow: hidden;
  width: 100%;
  height: 100%;
}

.mag-flip-page img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
  pointer-events: none;
}

.mag-flip-page-back {
  background: linear-gradient(135deg, #8a1010, #5a0606);
}

.mag-page-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #e8e8e8;
  color: #999;
  font-size: 24px;
}

.stf__parent {
  margin: 0 auto;
}

.stf__block {
  perspective: 2000px;
}

.magazine-reader__flipbook .stf__item.mag-cover-page.--simple {
  transform: translateX(0) !important;
}

/* 左右箭头 */
.magazine-reader__nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 56px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(10px);
  border-radius: 10px;
  color: rgba(255, 255, 255, 0.6);
  cursor: pointer;
  z-index: 30;
  transition: all 0.25s;
  border: 1px solid rgba(255, 255, 255, 0.06);
}

.magazine-reader__nav:hover {
  background: rgba(160, 0, 0, 0.6);
  color: #fff;
  border-color: rgba(160, 0, 0, 0.8);
}

.magazine-reader__nav--left { left: 16px; }
.magazine-reader__nav--right { right: 16px; }

.magazine-reader__nav-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

/* 底部工具栏 */
.magazine-reader__footer {
  height: 100px;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(12px);
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 40px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
}

.magazine-reader__catalog-btn,
.magazine-reader__buy-btn {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #fff;
  font-size: 26px;
  font-family: 'Microsoft YaHei', sans-serif;
  cursor: pointer;
  transition: all 0.25s;
}

.magazine-reader__buy-btn {
  padding: 12px 24px;
  background: rgba(160, 0, 0, 0.85);
  border: 1px solid rgba(160, 0, 0, 0.9);
  border-radius: 50px;
}

.magazine-reader__buy-btn:hover {
  background: rgba(160, 0, 0, 1);
  box-shadow: 0 2px 16px rgba(160, 0, 0, 0.45);
}

/* 目录弹层 */
.magazine-reader__catalog {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  z-index: 1100;
  display: flex;
  align-items: flex-end;
  justify-content: center;
}

.magazine-reader__catalog-panel {
  width: var(--page-width, 804px);
  max-height: 70%;
  background: #f7f7f7;
  border-radius: 24px 24px 0 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.magazine-reader__catalog-header {
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-bottom: 1px solid #eeeeee;
  position: relative;
}

.magazine-reader__catalog-title {
  display: flex;
  align-items: center;
  gap: 12px;
  color: #333;
  font-size: 30px;
  font-family: 'Microsoft YaHei', sans-serif;
}

.magazine-reader__catalog-close {
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  width: 72px;
  height: 72px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
  cursor: pointer;
}

.magazine-reader__catalog-close svg {
  transform: rotate(-90deg);
  width: 32px;
  height: 32px;
}

.magazine-reader__catalog-list {
  overflow-y: auto;
  padding: 16px 32px 32px;
}

.magazine-reader__catalog-item {
  display: flex;
  align-items: center;
  padding: 20px 0;
  border-bottom: 1px solid #eeeeee;
  color: #333;
  font-size: 28px;
  font-family: 'Microsoft YaHei', sans-serif;
  cursor: pointer;
  transition: color 0.2s;
}

.magazine-reader__catalog-item:hover {
  color: #c00513;
}

.magazine-reader__catalog-page {
  width: 60px;
  color: #999;
  flex-shrink: 0;
}

.magazine-reader__catalog-type {
  width: 80px;
  color: #666;
  flex-shrink: 0;
}

.magazine-reader__catalog-name {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* 页面列表弹层 */
.magazine-reader__page-list {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  z-index: 1100;
  display: flex;
  align-items: flex-end;
  justify-content: center;
}

.magazine-reader__page-list-panel {
  width: var(--page-width, 804px);
  max-height: 70%;
  background: #f7f7f7;
  border-radius: 24px 24px 0 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.magazine-reader__page-list-header {
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-bottom: 1px solid #eeeeee;
  position: relative;
  color: #333;
  font-size: 30px;
  font-family: 'Microsoft YaHei', sans-serif;
}

.magazine-reader__page-list-title {
  display: flex;
  align-items: center;
  gap: 12px;
}

.magazine-reader__page-list-close {
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  width: 72px;
  height: 72px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
  cursor: pointer;
}

.magazine-reader__page-list-close svg {
  transform: rotate(-90deg);
  width: 32px;
  height: 32px;
}

.magazine-reader__page-list-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 24px 32px 32px;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
}

.magazine-reader__page-list-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  transition: transform 0.2s;
}

.magazine-reader__page-list-item:hover {
  transform: translateY(-4px);
}

.magazine-reader__page-list-item--active .magazine-reader__page-list-thumb {
  box-shadow: 0 0 0 3px #c00513;
}

.magazine-reader__page-list-item--active .magazine-reader__page-list-number {
  color: #c00513;
  font-weight: bold;
}

.magazine-reader__page-list-thumb {
  width: 100%;
  aspect-ratio: 615 / 795;
  background: #e8e8e8;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.magazine-reader__page-list-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  background: #444;
}

.magazine-reader__page-list-placeholder {
  color: #999;
  font-size: 22px;
}

.magazine-reader__page-list-number {
  color: #666;
  font-size: 24px;
  font-family: 'Microsoft YaHei', sans-serif;
}

/* 弹层动画 */
.magazine-reader__slide-enter-active,
.magazine-reader__slide-leave-active {
  transition: opacity 0.3s;
}

.magazine-reader__slide-enter-active .magazine-reader__catalog-panel,
.magazine-reader__slide-enter-active .magazine-reader__page-list-panel,
.magazine-reader__slide-leave-active .magazine-reader__catalog-panel,
.magazine-reader__slide-leave-active .magazine-reader__page-list-panel {
  transition: transform 0.3s ease-out;
}

.magazine-reader__slide-enter,
.magazine-reader__slide-leave-to {
  opacity: 0;
}

.magazine-reader__slide-enter .magazine-reader__catalog-panel,
.magazine-reader__slide-enter .magazine-reader__page-list-panel,
.magazine-reader__slide-leave-to .magazine-reader__catalog-panel,
.magazine-reader__slide-leave-to .magazine-reader__page-list-panel {
  transform: translateY(100%);
}

/* ===== 购买弹窗样式 ===== */
.mag-buy-dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(0.32rem);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 0.8rem;
  box-sizing: border-box;
  overflow-y: auto;
}

.mag-buy-dialog {
  background: #fff;
  border-radius: 0.48rem;
  box-shadow: 0 0.32rem 1.28rem rgba(0, 0, 0, 0.2);
  width: calc(100% - 0.8rem);
  max-width: 28rem;
  max-height: 90vh;
  overflow-y: auto;
  animation: mag-dialog-slide-up 0.3s ease-out;
}

@keyframes mag-dialog-slide-up {
  from {
    opacity: 0;
    transform: translateY(1.2rem);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.mag-dialog-header {
  padding: 0.96rem 0.96rem 0.64rem;
  text-align: center;
  border-bottom: 1px solid #eee;
}

.mag-dialog-header h3 {
  margin: 0;
  font-size: 0.8rem;
  font-weight: bold;
  color: #333;
  line-height: 1.4;
}

.mag-dialog-content {
  padding: 0.96rem;
}

.mag-select-section {
  margin-bottom: 0.96rem;
}

.mag-label {
  display: block;
  font-size: 0.56rem;
  color: #666;
  margin-bottom: 0.48rem;
  font-weight: 500;
}

.mag-radio-group {
  display: flex;
  gap: 0.96rem;
  flex-wrap: wrap;
}

.mag-radio-item {
  display: flex;
  align-items: center;
  cursor: pointer;
}

.mag-radio-item input[type="radio"] {
  width: 0.72rem;
  height: 0.72rem;
  margin-right: 0.24rem;
  accent-color: #A00000;
  flex-shrink: 0;
}

.mag-radio-text {
  font-size: 0.56rem;
  color: #333;
}

.mag-info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.8rem;
  padding: 0.48rem 0;
}

.mag-info-label {
  font-size: 0.56rem;
  color: #666;
}

.mag-info-value {
  font-size: 0.64rem;
  color: #333;
  font-weight: 500;
}

.mag-quantity-section {
  margin-bottom: 0.8rem;
}

.mag-quantity-control {
  display: flex;
  align-items: center;
  gap: 0.64rem;
}

.mag-qty-btn {
  width: 1.28rem;
  height: 1.28rem;
  border: 1px solid #ddd;
  background: #f5f5f5;
  border-radius: 0.24rem;
  font-size: 0.72rem;
  color: #333;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.mag-qty-btn:hover:not(:disabled) {
  background: #A00000;
  color: #fff;
  border-color: #A00000;
}

.mag-qty-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.mag-qty-value {
  font-size: 0.64rem;
  color: #333;
  font-weight: 500;
  min-width: 1.6rem;
  text-align: center;
}

.mag-total-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.8rem;
  background: #fafafa;
  border-radius: 0.32rem;
  margin-bottom: 0.96rem;
}

.mag-total-label {
  font-size: 0.64rem;
  color: #666;
}

.mag-total-value {
  font-size: 0.96rem;
  color: #A00000;
  font-weight: bold;
}

/* ===== 收货地址区域 ===== */
.mag-address-section {
  margin-top: 0.8rem;
  padding-top: 0.8rem;
  border-top: 1px solid #eee;
}

.mag-input-row,
.mag-district-section {
  display: flex;
  align-items: center;
  margin-bottom: 0.64rem;
}

.mag-input-row.mag-input-row-textarea {
  align-items: flex-start;
}

.mag-address-section .mag-label {
  display: inline-block;
  width: 4rem;
  flex-shrink: 0;
  font-size: 0.56rem;
  color: #333;
  font-weight: 400;
  margin-bottom: 0;
  line-height: 1.6rem;
  text-align: right;
  padding-right: 0.24rem;
  box-sizing: border-box;
}

.mag-input-row.mag-input-row-textarea .mag-label {
  line-height: 1.4;
  padding-top: 0.32rem;
}

.mag-input-text,
.mag-input-textarea {
  flex: 1;
  min-width: 0;
  width: auto;
  box-sizing: border-box;
}

.mag-input-text {
  padding: 0 0.32rem;
  border: 1px solid #ddd;
  border-radius: 0.24rem;
  font-size: max(0.56rem, 16px);
  color: #333;
  transition: all 0.2s;
  height: 1.6rem;
  line-height: 1.6rem;
  box-sizing: border-box;
}

.mag-input-text:focus {
  outline: none;
  border-color: #A00000;
  box-shadow: 0 0 0 2px rgba(160, 0, 0, 0.1);
}

.mag-input-textarea {
  padding: 0.32rem;
  border: 1px solid #ddd;
  border-radius: 0.24rem;
  font-size: max(0.56rem, 16px);
  color: #333;
  resize: vertical;
  font-family: inherit;
  min-height: 3.2rem;
  line-height: 1.4;
  box-sizing: border-box;
}

.mag-input-textarea:focus {
  outline: none;
  border-color: #A00000;
  box-shadow: 0 0 0 2px rgba(160, 0, 0, 0.1);
}

/* 省市区选择行 */
.mag-district-section {
  flex-wrap: wrap;
}

.mag-district-row {
  display: flex;
  gap: 0.16rem;
  flex: 1;
  align-items: center;
  min-width: 0;
}

/* ===== 自定义下拉（Trigger） ===== */
.mag-custom-select {
  position: relative;
  flex: 1;
  min-width: 0;
}

.mag-custom-select--disabled {
  opacity: 0.5;
  pointer-events: none;
}

.mag-custom-select__trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.08rem;
  width: 100%;
  height: 1.6rem;
  padding: 0 0.24rem;
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 0.24rem;
  font-size: max(0.56rem, 16px);
  color: #333;
  cursor: pointer;
  box-sizing: border-box;
  transition: all 0.2s;
  overflow: hidden;
}

.mag-custom-select__trigger > span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mag-custom-select--active .mag-custom-select__trigger {
  color: #333;
}

.mag-custom-select__placeholder {
  color: #999;
}

.mag-custom-select__arrow {
  flex-shrink: 0;
  color: #666;
}

/* 警告文字 */
.mag-warning-text {
  flex-basis: 100%;
  margin-top: 0.32rem;
  margin-left: 3rem;
  padding: 0.32rem;
  background: #FFF3CD;
  border-left: 4px solid #FFCC00;
  color: #856404;
  font-size: 0.48rem;
  border-radius: 0.16rem;
  box-sizing: border-box;
}

.mag-pay-btn {
  width: 100%;
  height: 1.92rem;
  background: linear-gradient(135deg, #A00000, #700000);
  color: #fff;
  border: none;
  border-radius: 0.32rem;
  font-size: 0.64rem;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.25s;
  margin-top: 0.48rem;
}

.mag-pay-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #C00000, #900000);
  box-shadow: 0 0.16rem 0.8rem rgba(160, 0, 0, 0.4);
}

.mag-pay-btn:disabled {
  background: #ccc;
  color: #fff;
  cursor: not-allowed;
  box-shadow: none;
  opacity: 0.7;
}

.mag-back-btn {
  width: 100%;
  height: 1.92rem;
  background: transparent;
  color: #666;
  border: 1px solid #ddd;
  border-radius: 0.32rem;
  font-size: 0.56rem;
  cursor: pointer;
  transition: all 0.2s;
  margin-top: 0.48rem;
}

.mag-back-btn:hover {
  background: #f5f5f5;
  color: #333;
  border-color: #ccc;
}

.mag-order-summary {
  margin-bottom: 0.96rem;
  padding: 0.64rem;
  background: #f9f9f9;
  border-radius: 0.32rem;
  text-align: center;
}

.mag-order-summary p {
  margin: 0;
  font-size: 0.56rem;
  color: #666;
  line-height: 1.6;
}

.mag-pay-amount {
  font-size: 1.28rem;
  color: #A00000;
  font-weight: bold;
  text-align: center;
  margin-bottom: 0.96rem;
}

.mag-dialog-content--center {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 1.6rem 0.96rem;
}

.mag-success-icon {
  width: 2.4rem;
  height: 2.4rem;
  margin-bottom: 0.64rem;
}

.mag-success-icon svg {
  width: 100%;
  height: 100%;
}

.mag-success-title {
  margin: 0 0 0.48rem;
  font-size: 0.8rem;
  font-weight: bold;
  color: #333;
}

.mag-success-tip {
  margin: 0;
  font-size: 0.48rem;
  color: #999;
  line-height: 1.6;
}

.mag-success-link {
  color: #A00000;
  text-decoration: underline;
  cursor: pointer;
  margin: 0 0.08rem;
}

.mag-success-link:hover {
  color: #C00000;
}

.mag-dialog-fade-enter-active,
.mag-dialog-fade-leave-active {
  transition: opacity 0.3s ease;
}

.mag-dialog-fade-enter-from,
.mag-dialog-fade-leave-to {
  opacity: 0;
}

.mag-buy-dialog-select-enter-active,
.mag-buy-dialog-select-leave-active,
.mag-buy-dialog-pay-enter-active,
.mag-buy-dialog-pay-leave-active {
  transition: all 0.3s ease;
}

.mag-buy-dialog-select-enter-from,
.mag-buy-dialog-select-leave-to,
.mag-buy-dialog-pay-enter-from,
.mag-buy-dialog-pay-leave-to {
  opacity: 0;
  transform: translateX(-0.8rem);
}

/* ===== ActionSheet 选择器 ===== */
.mag-actionsheet-mask {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.8rem;
  box-sizing: border-box;
}

.mag-actionsheet {
  width: 100%;
  max-width: 22rem;
  max-height: 70vh;
  background: #fff;
  border-radius: 0.48rem;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 0.32rem 1.28rem rgba(0, 0, 0, 0.25);
}

.mag-actionsheet__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.64rem 0.8rem;
  border-bottom: 1px solid #eee;
  flex-shrink: 0;
  background: #fafafa;
}

.mag-actionsheet__cancel {
  font-size: 0.6rem;
  color: #666;
  cursor: pointer;
  padding: 0.16rem 0.32rem;
  border-radius: 0.16rem;
  transition: all 0.2s;
}

.mag-actionsheet__cancel:hover {
  color: #A00000;
  background: rgba(160, 0, 0, 0.08);
}

.mag-actionsheet__title {
  font-size: 0.64rem;
  font-weight: bold;
  color: #333;
}

.mag-actionsheet__placeholder {
  display: inline-block;
  width: 1.2rem;
}

.mag-actionsheet__body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 0.32rem 0;
}

.mag-actionsheet__item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.56rem 0.96rem;
  font-size: max(0.64rem, 16px);
  color: #333;
  cursor: pointer;
  transition: background 0.15s;
  line-height: 1.4;
}

.mag-actionsheet__item:hover {
  background: #f5f5f5;
}

.mag-actionsheet__item.is-active {
  color: #A00000;
  font-weight: bold;
  background: rgba(160, 0, 0, 0.06);
}

.mag-actionsheet__item-text {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mag-actionsheet__check {
  flex-shrink: 0;
  color: #A00000;
  margin-left: 0.32rem;
}

/* ActionSheet 遮罩动画 */
.mag-actionsheet-fade-enter-active,
.mag-actionsheet-fade-leave-active {
  transition: opacity 0.25s ease;
}

.mag-actionsheet-fade-enter-from,
.mag-actionsheet-fade-leave-to {
  opacity: 0;
}

/* ActionSheet 主体动画（居中缩放淡入） */
.mag-actionsheet-slide-enter-active,
.mag-actionsheet-slide-leave-active {
  transition: all 0.25s ease;
}

.mag-actionsheet-slide-enter-from,
.mag-actionsheet-slide-leave-to {
  opacity: 0;
  transform: scale(0.92);
}
</style>